//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useEffect } from 'react'

import { Box, Chip, Paper, Tooltip, Typography, useTheme, Button } from '@mui/material'
import { OpenInNew, Info } from '@mui/icons-material'

import { EthAddr } from '@shared/SchemaTypes'
import {
  PoolAccountCTO,
  PoolBaseAccountCTO,
  PoolDynamicAccountCTO,
  PoolDynamicOverviewCTO,
  PoolFlexAccountCTO,
  PoolFlexOverviewCTO,
  PoolOverviewCTO,
} from '@shared/sharedTypes'
import LogoUpload from '@react/components/LogoUpload'
import { useOtChainState, useOtSelector } from '@react/redux/store'

import { ERC20Wei, aprToDisplay, erc20WeiToCommaDecString, pptToUSDC } from '@shared/conversions'

import { PoolTypeEnum } from '@shared/Enums'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { buildLenderPoolSummary } from './lenderUtils'
import { TrendingUp } from '@mui/icons-material'
import { buildPills } from '@react/components/elements/InterestPills'
import { useNavigate } from 'react-router-dom'
import { displayName } from '@shared/PoolBaseCTO'

export default function LenderPoolInvestmentSummary(props: { poolAddr: EthAddr }): React.ReactElement {
  const poolAddr = props.poolAddr

  const theme = useTheme()

  const tokenBalanceTooltip = 'Available Vault Tokens. (Token Balance minus pending withdraw requests)'

  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null
  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null
  const {
    userConnection: { accountAddr },
    chainConfiguration,
  } = useOtChainState()

  const poolAccountCTOs = useOtSelector((state) => state.poolState.poolAccountCTOs)
  const poolBaseAccountCTO = accountAddr
    ? (poolAccountCTOs?.[poolAddr]?.[accountAddr] as PoolBaseAccountCTO)
    : undefined

  const poolAccountCTO =
    poolBaseAccountCTO?.poolType === PoolTypeEnum.Term ? (poolBaseAccountCTO as PoolAccountCTO) : null
  const poolFlexAccountCTO =
    poolBaseAccountCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseAccountCTO as PoolFlexAccountCTO) : null
  const poolDynamicAccountCTO =
    poolBaseAccountCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseAccountCTO as PoolDynamicAccountCTO) : null

  const navigate = useNavigate()

  useEffect(() => {
    if (!poolBaseOverviewCTO || !poolBaseAccountCTO) {
      const timeout = setTimeout(() => {
        if (!poolBaseOverviewCTO || !poolBaseAccountCTO) {
          navigate('/')
        }
      }, 1000)

      return () => clearTimeout(timeout)
    }
  }, [poolBaseOverviewCTO, navigate])

  if (!poolBaseOverviewCTO || !poolBaseAccountCTO) {
    return <></>
  }
  let investorSummary
  let month
  let valueAtMaturity
  let matureDate
  if (poolAccountCTO && poolOverviewCTO) {
    investorSummary = buildLenderPoolSummary(poolOverviewCTO, poolAccountCTO)
    matureDate = new Date(buildLenderPoolSummary(poolOverviewCTO, poolAccountCTO).matureEthDate * 1e3)

    month = matureDate?.toLocaleString('default', { month: 'short' })

    const valueAtMaturityWithoutWithdraws =
      poolOverviewCTO &&
      pptToUSDC(
        (
          BigInt(poolAccountCTO?.tokenBalance) -
          BigInt(poolAccountCTO.requestedSharesOf) -
          BigInt(poolAccountCTO.redeemableSharesOf)
        ).toString() as ERC20Wei,
        poolOverviewCTO?.exchangeRateAtMaturity ?? 1e18
      )
    valueAtMaturity = (
      BigInt(valueAtMaturityWithoutWithdraws) +
      BigInt(poolAccountCTO?.requestedAssetsOf) +
      BigInt(poolAccountCTO?.withdrawableAssetsOf)
    ).toString() as ERC20Wei
  }
  if (poolFlexAccountCTO) {
    valueAtMaturity = BigInt(poolFlexAccountCTO?.accountOutstandingLoanPrincipals).toString() as ERC20Wei
  }
  if (poolDynamicAccountCTO) {
    valueAtMaturity = BigInt(poolDynamicAccountCTO?.principalEarningInterest).toString() as ERC20Wei
  }

  //const valuePrincipal =   pptToUSDC(poolAccountCTO?.balance, poolOverviewCTO?.activeLoanState?.exchangeRateAtDeposit ?? 1e18)

  return (
    <Paper sx={{ marginTop: 4, marginBottom: 4 }}>
      <Box
        sx={{
          display: 'flex',
          padding: '24px',
          flexGrow: 1,
          flexDirection: 'row',
        }}
      >
        <Box p={3} m={3} sx={{ flexDirection: 'column', justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ mb: 1 }}>
              <LogoUpload size={50} addrOrId={poolAddr} />
            </Box>
            <Typography variant="bodyMuted" align="center">
              {poolBaseOverviewCTO.symbol}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
            alignContent: 'space-between',
            justifyContent: 'center',
          }}
        >
          <Tooltip
            title={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #dadde9',
                }}
              >
                <Typography variant="caption" color="inherit">
                  {' '}
                  {poolAddr}
                </Typography>
                <Typography variant="overline" margin="dense">
                  {displayName(poolBaseOverviewCTO)}
                </Typography>
              </Box>
            }
          >
            <Typography sx={{ textAlign: 'left', marginTop: 3 }} variant="h5" margin="dense">
              {displayName(poolBaseOverviewCTO)}
            </Typography>
          </Tooltip>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ display: 'inline', textAlign: 'left', marginTop: 1 }} variant="h2" margin="dense">
              {erc20WeiToCommaDecString(valueAtMaturity)}
            </Typography>
            <Typography display="inline" align={'left'} variant="h5Bold">
              {' '}
              {poolBaseOverviewCTO.liquidityTokenSymbol}{' '}
            </Typography>
          </Box>

          {poolDynamicOverviewCTO && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ textAlign: 'left', marginTop: 1 }} variant="body" margin="dense">
                  {erc20WeiToCommaDecString(poolDynamicAccountCTO?.maxRedeemRequest)} {poolBaseOverviewCTO.symbol}
                </Typography>
                <Tooltip title={tokenBalanceTooltip}>
                  <Info sx={{ fontSize: 16, color: theme.palette.text.muted, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              {buildPills(poolDynamicOverviewCTO, theme)}
            </Box>
          )}

          {poolFlexOverviewCTO && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ textAlign: 'left', marginTop: 1 }} variant="body" margin="dense">
                  {erc20WeiToCommaDecString(poolFlexAccountCTO?.maxRedeemRequest)} {poolBaseOverviewCTO.symbol}
                </Typography>
                <Tooltip title={tokenBalanceTooltip}>
                  <Info sx={{ fontSize: 16, color: theme.palette.text.muted, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              {buildPills(poolFlexOverviewCTO, theme)}
            </Box>
          )}

          {poolOverviewCTO && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ textAlign: 'left', marginTop: 1 }} variant="body" margin="dense">
                  {erc20WeiToCommaDecString(poolAccountCTO?.maxRedeemRequest)} {poolBaseOverviewCTO.symbol}
                </Typography>
                <Tooltip title={tokenBalanceTooltip}>
                  <Info sx={{ fontSize: 16, color: theme.palette.text.muted, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                {poolOverviewCTO.activeLoanState.loanAddr !== ETH_ADDR_ZERO &&
                  Number(investorSummary?.finalizedApr) !== 0 && (
                    <Chip
                      sx={{ backgroundColor: theme.palette.success.subtle, fontWeight: 700, marginRight: 2 }}
                      icon={<TrendingUp />}
                      label={aprToDisplay(investorSummary?.finalizedApr) + '% APY'}
                    />
                  )}
                {poolOverviewCTO.requestedLoanState.loanAddr !== ETH_ADDR_ZERO &&
                  Number(poolOverviewCTO?.requestedLoanState?.indicativeApr) !== 0 && (
                    <Chip
                      sx={{ backgroundColor: theme.palette.accent1.subtle, fontWeight: 700 }}
                      icon={<TrendingUp />}
                      label={`Indicative ${aprToDisplay(poolOverviewCTO?.requestedLoanState?.indicativeApr)}% APY`}
                    />
                  )}
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            paddingRight: '24px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => {
                const explorerUrl = chainConfiguration?.blockExplorerUrl
                  ? `${chainConfiguration.blockExplorerUrl}address/${poolAddr}`
                  : ''
                if (explorerUrl) {
                  window.open(explorerUrl, '_blank', 'noopener,noreferrer')
                }
              }}
            >
              View on explorer
            </Button>
          </Box>

          {poolOverviewCTO && (
            <Typography sx={{ textAlign: 'left', marginTop: 1 }} variant={'caption'}>
              Maturing
            </Typography>
          )}
          {poolOverviewCTO && investorSummary && investorSummary.matureEthDate > 0 && (
            <>
              <Typography sx={{ textAlign: 'left', marginTop: 1 }} variant={'leadBold'}>
                {matureDate?.getDate().toString().padStart(2, '0')}
              </Typography>
              <Typography sx={{ textAlign: 'left', marginTop: 0 }} variant={'leadBold'}>
                {month?.toUpperCase()}
              </Typography>
              <Typography sx={{ textAlign: 'left', marginTop: 0 }} variant={'bodyBold'}>
                {matureDate?.getFullYear()}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  )
}
